import React from "react";
import Select from "react-select";
import { LuAlertCircle, LuLayers, LuLightbulb } from "react-icons/lu";

import { CuttingPieces, owedChipClass } from "./CuttingPieces";
import MaterialPhoto from "../materialLines/MaterialPhoto";
import StockShortNote from "../materialStocks/StockShortNote";
import cx from "../ui/cx";
import {
  CuttingDemand,
  barsNeeded,
  piecesOf,
  planCutting,
  poolOf,
  toUnits,
} from "../../static/cuttingLine";
import { sanitizeQty } from "../../static/materialLine";
import { compactSelectStyles } from "../../static/styles";
import {
  CuttingBarsPayload,
  CuttingMaterialInput,
  CuttingPieceProps,
  CuttingSkipPieceProps,
  CuttingStockProps,
} from "../../types/cuttingOrder";
import {
  MaterialOptionProps,
  MaterialOptionSizeProps,
} from "../../types/materialLine";

// One material the order cuts: the party's stock of it before the order, and this order's lines of it with the pieces
// each cuts. lineId is the saved line's id (null for a new one): lines of one cut length are cut in that order.
export type CuttingMaterialNeed = {
  materialId: number;
  name: string;
  material: MaterialOptionProps | undefined;
  stock: CuttingStockProps | undefined;
  lines: {
    key: string;
    name: string;
    cutLength: number;
    qty: number;
    lineId: number | null;
  }[];
  // Bars of more sizes an order saved before one size per material sent ("4600mm × 2"); a save drops them.
  dropped: string[];
};

// What one material's card works out from the form's choice for it.
export type CuttingMaterialCut = {
  sizes: MaterialOptionSizeProps[];
  // The bar size sent: the one picked, else the first (sizes come smallest first); null without sizes.
  size: MaterialOptionSizeProps | null;
  // The lengths of the party's pieces the order leaves alone, as the stock lists them (only lengths it holds).
  skipped: number[];
  // The fewest bars of the size that, with the pieces the order may use, cut every piece of its lines; null when no
  // number of them does.
  required: number | null;
  // Whether the bars box follows the required count (nothing typed in it).
  auto: boolean;
  text: string;
  qty: number;
  // What the party holds after the order, for its next order: what the cutting leaves and the pieces left alone.
  after: CuttingPieceProps[];
  // The pieces of the order's lines no material is left for.
  short: number;
  // Why the order can't be saved with these bars, or null.
  problem: string | null;
};

const noInput: CuttingMaterialInput = { skip: [], sizeId: null, qty: null };

const sum = (values: number[]) =>
  values.reduce((total, value) => total + value, 0);

// The card's sums, the same way CuttingStock cuts the order once it is saved: the party's pieces but those left alone,
// plus the bars of the chosen size; older orders' owed pieces first, then the lines, longest cut first (lines of one
// length by saved line, then as listed). An untouched bars box takes the fewest bars that cut every line.
export const materialCut = (
  row: CuttingMaterialNeed,
  input: CuttingMaterialInput | undefined,
  scrapBelow: number
): CuttingMaterialCut => {
  const { skip, sizeId, qty: typed } = input || noInput;
  const sizes = row.material?.sizes || [];
  const size = sizes.find((item) => item.id === sizeId) || sizes[0] || null;
  const barSet = new Set(
    [
      ...(row.stock?.bar_lengths || []),
      ...sizes.map((item) => item.name),
    ].map(toUnits)
  );
  const skipUnits = new Set(skip.map(toUnits));
  const pieces = row.stock?.pieces || [];
  const isSkipped = (piece: CuttingPieceProps) =>
    skipUnits.has(toUnits(piece.length));
  const start = poolOf(pieces.filter((piece) => !isSkipped(piece)));
  const owed = row.stock?.owed || [];

  const demands: CuttingDemand<null>[] = [
    ...owed.map((piece) => ({
      cut: toUnits(piece.cut_length),
      qty: piece.qty,
      scrap: toUnits(piece.scrap_below),
      ref: null,
    })),
    ...row.lines
      .map((line, index) => ({ line, index }))
      .sort(
        (a, b) =>
          toUnits(b.line.cutLength) - toUnits(a.line.cutLength) ||
          (a.line.lineId ?? Infinity) - (b.line.lineId ?? Infinity) ||
          a.index - b.index
      )
      .map(({ line }) => ({
        cut: toUnits(line.cutLength),
        qty: line.qty,
        scrap: scrapBelow,
        ref: null,
      })),
  ];
  const own = owed.length;
  const bar = size ? toUnits(size.name) : null;

  const required =
    bar !== null
      ? barsNeeded(start, barSet, demands, own, bar)
      : planCutting(start, barSet, demands)
          .short.slice(own)
          .some((value) => value > 0)
      ? null
      : 0;

  const auto = typed === null;
  const text = auto ? (required === null ? "" : String(required)) : typed;
  const qty = bar !== null ? Number(text) || 0 : 0;

  const pool = new Map(start);

  if (bar !== null && qty > 0) pool.set(bar, (pool.get(bar) || 0) + qty);

  const plan = planCutting(pool, barSet, demands);
  const left = new Map(plan.pool);

  poolOf(pieces.filter(isSkipped)).forEach((count, units) =>
    left.set(units, (left.get(units) || 0) + count)
  );

  const short = sum(plan.short.slice(own));

  return {
    sizes,
    size,
    skipped: pieces.filter(isSkipped).map((piece) => piece.length),
    required,
    auto,
    text,
    qty,
    after: piecesOf(left, barSet),
    short,
    problem:
      short === 0
        ? null
        : !size
        ? "આ મટીરીયલ ની કોઈ સાઈઝ નથી, પહેલા મટીરીયલ માં સાઈઝ ઉમેરો"
        : required === null
        ? "આ સાઈઝ ના બાર માંથી બધા નંગ કપાય નહીં, બીજી સાઈઝ પસંદ કરો"
        : `${short} નંગ માટે મટીરીયલ ખૂટે છે, ઓછામાં ઓછા ${required} બાર મોકલો`,
  };
};

// The bars the order sends, as the API takes them: each material's chosen size, when it has bars.
export const barsPayload = (
  needs: CuttingMaterialNeed[],
  cuts: Map<number, CuttingMaterialCut>
): CuttingBarsPayload[] =>
  needs.flatMap((row) => {
    const cut = cuts.get(row.materialId);

    return cut?.size && cut.qty > 0
      ? [
          {
            material_id: row.materialId,
            material_size_id: cut.size.id,
            qty: cut.qty,
          },
        ]
      : [];
  });

// The party's pieces the order leaves alone, as the API takes them: only lengths the party holds.
export const skipPayload = (
  needs: CuttingMaterialNeed[],
  cuts: Map<number, CuttingMaterialCut>
): CuttingSkipPieceProps[] =>
  needs.flatMap((row) =>
    (cuts.get(row.materialId)?.skipped || []).map((length) => ({
      material_id: row.materialId,
      length,
    }))
  );

type SizeOption = { value: number; label: string };

const barSelectStyles = compactSelectStyles<SizeOption>();

const rowClass = "flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-3";

const rowLabelClass =
  "w-24 shrink-0 pt-0.5 text-sm font-medium text-gray-500";

// The bars box, with a red border while it holds fewer bars than the order needs.
const barInputClass = (invalid: boolean) =>
  cx(
    "h-9 w-20 rounded-md border bg-white px-3 text-right text-sm font-semibold placeholder:text-muted-foreground focus:outline-none focus:ring-1",
    invalid
      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:border-[#113F67] focus:ring-[#113F67]"
  );

type CardProps = {
  row: CuttingMaterialNeed;
  cut: CuttingMaterialCut;
  sizeUnit: string;
  isLoading: boolean;
  onToggleSkip: (materialId: number, length: number) => void;
  onSelectSize: (materialId: number, sizeId: number) => void;
  // null puts the box back on the required count.
  onChangeQty: (materialId: number, text: string | null) => void;
};

// One material: what the party holds (each piece ticked to be used, or unticked to stay with the party), the bars sent
// (one size, the box filled with what the order needs), and the pieces that stay with the party for its next order. How
// the pieces are cut and the scrap aren't shown (the user wants just the inputs and what stays).
const MaterialCard = ({
  row,
  cut,
  sizeUnit,
  isLoading,
  onToggleSkip,
  onSelectSize,
  onChangeQty,
}: CardProps) => {
  const stock = row.stock?.pieces || [];
  const owed = row.stock?.owed || [];
  const problem = isLoading ? null : cut.problem;
  const sizeOptions: SizeOption[] = cut.sizes.map((size) => ({
    value: size.id,
    label: `${size.name}${sizeUnit}`,
  }));

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center gap-3">
        <MaterialPhoto
          name={row.name}
          imageUrl={row.material?.image_url}
          className="h-10 w-10 shrink-0 rounded-md"
          compact
        />
        <span className="min-w-0 flex-1 truncate font-semibold text-[#113F67]">
          {row.name}
        </span>
      </div>

      <div className={rowClass}>
        <span className={rowLabelClass}>પાર્ટી પાસે</span>
        {isLoading ? (
          <span className="text-sm text-gray-400">...</span>
        ) : (
          <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
            <CuttingPieces
              pieces={stock}
              sizeUnit={sizeUnit}
              skipped={cut.skipped}
              onToggle={(length) => onToggleSkip(row.materialId, length)}
            />
            {owed.map((piece) => (
              <span
                key={`${piece.order_id}:${piece.line_id}`}
                className={owedChipClass}
              >
                ઓર્ડર #{piece.order_id} ના {piece.name} {piece.qty} નંગ બાકી
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={rowClass}>
        <span className={cx(rowLabelClass, "sm:pt-2")}>બાર મોકલો</span>
        <div className="min-w-0 flex-1 space-y-2">
          {!cut.size ? (
            <p className="text-sm text-gray-500 sm:pt-2">
              આ મટીરીયલ ની કોઈ સાઈઝ નથી
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {sizeOptions.length > 1 ? (
                <span className="w-32 shrink-0">
                  <Select<SizeOption, false>
                    aria-label={`${row.name} બાર ની સાઈઝ`}
                    options={sizeOptions}
                    value={
                      sizeOptions.find(
                        (option) => option.value === cut.size?.id
                      ) || null
                    }
                    onChange={(option) => {
                      if (option && option.value !== cut.size?.id) {
                        onSelectSize(row.materialId, option.value);
                      }
                    }}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    styles={barSelectStyles}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </span>
              ) : (
                <span className="w-20 shrink-0 font-medium text-gray-700">
                  {cut.size.name}
                  {sizeUnit}
                </span>
              )}
              <span className="text-gray-400">×</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="0"
                aria-label={`${row.name} બાર`}
                aria-invalid={!!problem}
                value={cut.text}
                onChange={(event) =>
                  onChangeQty(
                    row.materialId,
                    sanitizeQty(event.target.value).slice(0, 6)
                  )
                }
                className={barInputClass(!!problem)}
              />
              <span className="text-gray-500">બાર</span>
              {!isLoading &&
                !cut.auto &&
                cut.required !== null &&
                cut.qty !== cut.required && (
                  <button
                    type="button"
                    title="ઓર્ડર ના બધા નંગ કાપવા જોઈતા બાર ભરો"
                    className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                    onClick={() => onChangeQty(row.materialId, null)}
                  >
                    <LuLightbulb className="h-3.5 w-3.5" />
                    જરૂરી {cut.required} બાર
                  </button>
                )}
            </div>
          )}
          {!isLoading && <StockShortNote size={cut.size} qty={cut.qty} />}
          {row.dropped.length > 0 && (
            <p className="text-xs text-orange-700">
              આ ઓર્ડર સાથે {row.dropped.join(", ")} બાર પણ મોકલેલ છે, સેવ કરતાં તે
              નીકળી જશે
            </p>
          )}
        </div>
      </div>

      {problem && (
        <p className="flex items-start gap-1 text-xs font-medium text-red-600">
          <LuAlertCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" />
          {problem}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3 text-sm">
        <span className="text-gray-600">ઓર્ડર પછી પાર્ટી પાસે:</span>
        {isLoading ? (
          <span className="text-gray-400">...</span>
        ) : (
          <CuttingPieces pieces={cut.after} sizeUnit={sizeUnit} />
        )}
      </div>
    </div>
  );
};

type Props = {
  needs: CuttingMaterialNeed[];
  // Each material's card sums (materialCut), by material id.
  cuts: Map<number, CuttingMaterialCut>;
  onToggleSkip: (materialId: number, length: number) => void;
  onSelectSize: (materialId: number, sizeId: number) => void;
  onChangeQty: (materialId: number, text: string | null) => void;
  // An order being edited that has orders after it: those are cut again once it is saved.
  later: boolean;
  sizeUnit: string;
  isLoading: boolean;
};

// The material the order cuts, one full-width card per material (CuttingStock on the backend works the same out once
// the order is saved, and refuses it while a line can't be cut).
const CuttingMaterialsSent = ({
  needs,
  cuts,
  onToggleSkip,
  onSelectSize,
  onChangeQty,
  later,
  sizeUnit,
  isLoading,
}: Props) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2">
      <LuLayers className="h-5 w-5 text-[#113F67]" />
      <h3 className="text-lg font-semibold text-[#113F67]">મટીરીયલ</h3>
    </div>

    {later && needs.length > 0 && (
      <p className="text-xs text-orange-700">
        આ ઓર્ડર પછી ના ઓર્ડર નું કટીંગ સેવ કર્યા પછી ફરીથી ગણાશે
      </p>
    )}

    {needs.length === 0 ? (
      <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
        આઈટમ ઉમેરતાં તેના મટીરીયલ નું કટીંગ અહીં દેખાશે
      </div>
    ) : (
      needs.map((row) => {
        const cut = cuts.get(row.materialId);

        return (
          cut && (
            <MaterialCard
              key={row.materialId}
              row={row}
              cut={cut}
              sizeUnit={sizeUnit}
              isLoading={isLoading}
              onToggleSkip={onToggleSkip}
              onSelectSize={onSelectSize}
              onChangeQty={onChangeQty}
            />
          )
        );
      })
    )}
  </div>
);

export default CuttingMaterialsSent;
